import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import GroupPreviewScreen from './GroupPreviewScreen';
import CreateAccountScreen from './CreateAccountScreen';
import ContributionAgreementScreen from './ContributionAgreementScreen';
import MemberSuccessScreen from './MemberSuccessScreen';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { checkFeatureFlag, FEATURE_FLAGS } from '../../../shared/utils/featureFlags';

const MemberOnboardingWizard = ({ groupId: propGroupId, onComplete }) => {
  const { groupId: urlGroupId } = useParams();
  const groupId = propGroupId || urlGroupId;
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [groupData, setGroupData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const totalSteps = 4;

  // Fetch group data on component mount
  useEffect(() => {
    const fetchGroupData = async () => {
      try {
        if (!groupId) {
          console.error('❌ No groupId provided');
          setError('Invalid invite link - no group ID');
          return;
        }
        
        const { data: group, error: groupError } = await supabaseClient
          .from('groups')
          .select('id, group_name, cultural_term, contribution_frequency, contribution_amount_per_hand, start_date, status')
          .eq('id', parseInt(groupId))
          .single();

        if (groupError) {
          console.error('❌ Supabase error:', groupError);
          throw groupError;
        }

        if (!group) {
          console.error('❌ No group data returned');
          throw new Error('Group not found');
        }
        setGroupData(group);
        setFormData(prev => ({ 
          ...prev, 
          groupName: group.group_name,
          amount: group.contribution_amount_per_hand,
          frequency: group.contribution_frequency
        }));

      } catch (err) {
        console.error('❌ Error fetching group data:', err);
        setError('Group not found or invalid invite link');
      }
    };

    if (groupId) {
      fetchGroupData();
    } else {
      console.error('❌ No groupId available for fetch');
      setError('Invalid invite link');
    }
  }, [groupId]);

  const updateFormData = (newData) => {
    setFormData(prev => ({ ...prev, ...newData }));
  };

  const handleNext = (stepData) => {
    updateFormData(stepData);
    
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async (finalData) => {
    setIsLoading(true);
    setError(null);

    try {
      const completeData = { ...formData, ...finalData };

      // Step 1: Create Supabase auth account so the member can log in
      const tempPassword = `EsuSol_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}!`;
      const { data: authData, error: authError } = await supabaseClient.auth.signUp({
        email: completeData.email,
        password: tempPassword,
        options: {
          data: { full_name: completeData.name, role: 'member' }
        }
      });

      if (authError) throw new Error(`Account creation failed: ${authError.message}`);

      // Step 2: Insert member record linked to the auth account
      const { data: member, error: memberError } = await supabaseClient
        .from('members')
        .insert({
          group_id: parseInt(groupId, 10),
          // first/last from the agreement step are authoritative; keep name as
          // "First Last" so single-field readers keep working.
          first_name: completeData.firstName || null,
          last_name: completeData.lastName || null,
          name: (completeData.firstName && completeData.lastName)
            ? `${completeData.firstName.trim()} ${completeData.lastName.trim()}`
            : completeData.name,
          whatsapp_number: completeData.whatsappNumber,
          sms_number: completeData.smsNumber,
          email: completeData.email,
          notification_preference: completeData.notificationPreference,
          display_alias: completeData.displayAlias?.trim().slice(0, 40) || null,   // optional (Stage 46)
          zelle_identifier: completeData.zelleIdentifier?.trim() || null,
          cashapp_handle: completeData.cashappHandle?.trim() || null,
          venmo_handle: completeData.venmoHandle?.trim() || null,
          identity_verified: false,
          joined_date: new Date().toISOString().split('T')[0],
          status: 'active',
          auth_user_id: authData.user.id
        })
        .select()
        .single();

      if (memberError) throw new Error(`Failed to create member profile: ${memberError.message}`);

      // Step 2b: PERSIST CONSENT (self-register path). The member just created
      // their account (Step 1) and owns this member row (auth_user_id set above),
      // so record_consent_self is authorized by auth.uid(). We do this right after
      // member creation and BLOCK completion on failure — no member finishes the
      // join past a consent we didn't record. The signature/agreed/version/hash
      // come from the shared agreement screen (same canonical terms + hash).
      const { error: consentError } = await supabaseClient.rpc('record_consent_self', {
        p_group_id: parseInt(groupId, 10),
        p_member_db_id: member.id,
        p_signature: completeData.signature,
        p_agreed: completeData.agreed,
        p_terms_version: completeData.termsVersion,
        p_terms_sha256: completeData.termsSha256,
        p_client_signed_at: completeData.signedAt,
        p_privacy_accepted: completeData.privacyAccepted,
        p_privacy_version: completeData.privacyVersion,
        p_tc_accepted: completeData.tcAccepted,
        p_tc_version: completeData.tcVersion,
        p_messaging_accepted: completeData.messagingAccepted,
        p_messaging_version: completeData.messagingVersion,
        p_messaging_method: completeData.messagingMethod,
      });
      if (consentError) {
        throw new Error(
          'We could not record your agreement, so we can\'t finish joining yet. ' +
          'Please try again, or contact your group admin if it keeps happening.'
        );
      }

      // Step 3: Audit log
      await supabaseClient.from('audit_logs').insert({
        timestamp: new Date().toISOString(),
        event_category: 'member_management',
        event_type: 'member_joined',
        actor_id: authData.user.id,
        actor_role: 'member',
        group_id: parseInt(groupId, 10),
        new_value: {
          member_name: completeData.name,
          group_id: parseInt(groupId, 10)
        },
        metadata: {
          source: 'member_onboarding_wizard',
          signature: completeData.signature
        },
        risk_level: 'low',
        flagged: false
      });

      // Step 5: Notify admin via audit log
      const { data: groupInfo } = await supabaseClient
        .from('groups')
        .select('billing_admin_id, group_name')
        .eq('id', parseInt(groupId, 10))
        .single();

      await supabaseClient.from('audit_logs').insert({
        event_category: 'member_management',
        event_type: 'member_joined_needs_hands',
        actor_id: authData.user.id,
        actor_role: 'system',
        group_id: parseInt(groupId, 10),
        new_value: {
          message: `New member ${completeData.name} has joined your group ${groupInfo?.group_name} via invite link. Remember to assign their hands and rotation position.`,
          member_name: completeData.name,
          group_name: groupInfo?.group_name
        },
        risk_level: 'low',
        flagged: false,
        timestamp: new Date().toISOString()
      });

      setCurrentStep(totalSteps);
      updateFormData({ memberId: member.id });
      setIsLoading(false);

    } catch (err) {
      console.error('Error creating member:', err);
      setError(err.message || 'Failed to join group. Please try again.');
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    const stepProps = {
      data: formData,
      groupData: groupData,
      onNext: currentStep === 3 ? handleComplete : handleNext,
      onBack: handleBack
    };

    switch (currentStep) {
      case 1:
        return <GroupPreviewScreen groupData={groupData} onNext={() => handleNext()} />;
      case 2:
        return <CreateAccountScreen {...stepProps} />;
      case 3:
        return <ContributionAgreementScreen {...stepProps} submitting={isLoading} />;
      case 4:
        return <MemberSuccessScreen data={formData} onComplete={onComplete} navigate={navigate} />;
      default:
        return <GroupPreviewScreen groupData={groupData} onNext={() => handleNext()} />;
    }
  };

  const renderProgressIndicator = () => {
    if (currentStep === 1 || currentStep === 4) return null;

    return (
      <div className="flex justify-center space-x-2 mb-6">
        {Array.from({ length: totalSteps - 1 }, (_, index) => (
          <div
            key={index + 1}
            className={`h-2 w-8 rounded-full transition-colors ${
              index + 1 < currentStep
                ? 'bg-primary-600'
                : index + 1 === currentStep
                ? 'bg-primary-600'
                : 'bg-neutral-300'
            }`}
          />
        ))}
      </div>
    );
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-error-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-neutral-900 mb-2">Invalid Invite Link</h2>
          <p className="text-neutral-600">{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading && currentStep !== 4) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {renderProgressIndicator()}
      
      {renderStep()}
      
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-neutral-100 rounded-lg p-6 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            <span className="text-neutral-700">Joining group...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberOnboardingWizard;
